import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

export default function CategorySection() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <section id="categories" className="w-full bg-background-50 py-14 md:py-20">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <div className="text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">
            {t('common.categoriesTitle')}
          </h2>
          <p className="mt-3 text-sm md:text-base text-foreground-600">
            {t('common.categoriesSubtitle')}
          </p>
        </div>

        <div className="relative mt-8 max-w-md mx-auto" ref={ref}>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="w-full flex items-center justify-between gap-3 px-5 py-3.5 rounded-lg border border-background-300 bg-background-50 text-foreground-950 text-sm font-medium hover:border-primary-400 transition-colors"
          >
            <span className="flex items-center gap-2">
              <i className="ri-menu-2-line text-lg text-foreground-500"></i>
              {t('common.browseCategories')}
            </span>
            <i className={`ri-arrow-down-s-line text-lg text-foreground-500 transition-transform ${open ? 'rotate-180' : ''}`}></i>
          </button>

          {open && (
            <div className="absolute left-0 right-0 mt-2 z-30 rounded-lg border border-background-200 bg-background-50 py-1 max-h-96 overflow-y-auto">
              {categories.map((cat) => (
                <a
                  key={cat.key}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors"
                >
                  <img
                    src={cat.image}
                    alt={t(`categories.${cat.key}`)}
                    title={`${t(`categories.${cat.key}`)} — USFans`}
                    className="w-9 h-9 rounded-md object-cover object-top"
                  />
                  <span className="flex-1">{t(`categories.${cat.key}`)}</span>
                  <i className="ri-arrow-right-up-line text-xs text-foreground-400"></i>
                </a>
              ))}
            </div>
          )}
        </div>

        <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {categories.map((cat) => (
            <a
              key={cat.key}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group overflow-hidden rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 transition-colors"
            >
              <div className="relative aspect-square overflow-hidden">
                <img
                  src={cat.image}
                  alt={t(`categories.${cat.key}`)}
                  title={`${t(`categories.${cat.key}`)} — USFans`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="flex items-center justify-between gap-2 px-3.5 py-3">
                <span className="text-sm text-foreground-900 font-medium leading-tight whitespace-nowrap overflow-hidden text-ellipsis">
                  {t(`categories.${cat.key}`)}
                </span>
                <span className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-700 group-hover:bg-primary-100 group-hover:text-primary-700 transition-colors">
                  <i className={`${cat.icon} text-sm`}></i>
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}